import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { approveDevice, denyDevice, getProfile, getSession, isUnauthenticated, listDeviceApprovals, listDevices, listSecurityEvents, listSessions, registerAccount, restoreDevice, retireDevice, revokeBinding, revokeDevice, revokeSession, sendOTP, signIn, signOut, verifyOTP, } from "@empite/api-client";
import { sessionKeys } from "./keys.js";
import { useTransport } from "./transport-context.js";
/**
 * useSession answers "is anyone signed in", and answers it as DATA rather
 * than as an error.
 *
 * GET /v1/auth/sso/session returns 401 when there is no session, which is
 * correct HTTP and a terrible React query: every page would render an error
 * state on the ordinary path of a signed-out visitor. So the 401 is folded
 * into `null` here, once, and every caller downstream sees "no session"
 * instead of "something went wrong".
 */
export function useSession() {
    const transport = useTransport();
    return useQuery({
        queryKey: sessionKeys.session,
        queryFn: async () => {
            try {
                return await getSession(transport);
            }
            catch (err) {
                if (isUnauthenticated(err)) {
                    return null;
                }
                throw err;
            }
        },
        // Not signed in is a fast, cheap answer that changes only when the user
        // acts, and retrying it three times just delays the sign-in screen.
        retry: false,
        staleTime: 30_000,
    });
}
/** True only when a session is loaded and live — never during the first load,
 *  so a guard cannot flash the sign-in screen at a signed-in user. */
export function useIsSignedIn() {
    const query = useSession();
    return {
        ready: !query.isPending,
        signedIn: Boolean(query.data),
        session: query.data ?? null,
    };
}
export function useSignIn() {
    const transport = useTransport();
    const queries = useQueryClient();
    return useMutation({
        mutationFn: (body) => signIn(transport, { body }),
        // Everything account-scoped is now a different user's data. Invalidating
        // the whole subtree rather than just the session is what stops the
        // previous user's device list being shown to the next one.
        onSuccess: async () => {
            await queries.invalidateQueries({ queryKey: sessionKeys.session });
            await queries.invalidateQueries({ queryKey: sessionKeys.all });
        },
    });
}
export function useSignOut() {
    const transport = useTransport();
    const queries = useQueryClient();
    return useMutation({
        mutationFn: () => signOut(transport),
        // clear(), not invalidate(): an invalidated cache still holds the old
        // rows and re-renders them while the refetch is in flight, which shows a
        // signed-out browser the account it just left.
        onSettled: () => {
            queries.clear();
        },
    });
}
export function useRegister() {
    const transport = useTransport();
    return useMutation({ mutationFn: (body) => registerAccount(transport, { body }) });
}
export function useSendOTP() {
    const transport = useTransport();
    return useMutation({ mutationFn: (userID) => sendOTP(transport, { body: { user_id: userID } }) });
}
export function useVerifyOTP() {
    const transport = useTransport();
    const queries = useQueryClient();
    return useMutation({
        mutationFn: (body) => verifyOTP(transport, { body }),
        onSuccess: async () => {
            // Verifying advances registration_state, which is part of the session.
            await queries.invalidateQueries({ queryKey: sessionKeys.session });
        },
    });
}
export function useProfile() {
    const transport = useTransport();
    return useQuery({ queryKey: sessionKeys.profile, queryFn: () => getProfile(transport) });
}
/**
 * useSecurityEvents reads the account's audit trail.
 *
 * The page size is a parameter of the query KEY as well as of the request:
 * two components asking for different sizes are asking different questions,
 * and sharing one cache entry between them would show whichever landed last.
 *
 * No portal renders this today — the customer portal's security feed was
 * removed because a page of `token.refresh` rows buried the device cards a
 * person actually opens /security for. That is a rendering decision, not a
 * data one: `GET /account/security-events` is the person's own history and
 * still serves it, so the binding stays here rather than being rewritten by
 * whoever needs it next. Delete it only together with the endpoint.
 */
export function useSecurityEvents(limit) {
    const transport = useTransport();
    return useQuery({
        queryKey: [...sessionKeys.securityEvents, limit ?? null],
        queryFn: () => listSecurityEvents(transport, { limit }),
    });
}
export function useDevices() {
    const transport = useTransport();
    return useQuery({ queryKey: sessionKeys.devices, queryFn: () => listDevices(transport) });
}
export function useAppSessions() {
    const transport = useTransport();
    return useQuery({ queryKey: sessionKeys.sessions, queryFn: () => listSessions(transport) });
}
export function useDeviceApprovals() {
    const transport = useTransport();
    return useQuery({ queryKey: sessionKeys.deviceApprovals, queryFn: () => listDeviceApprovals(transport) });
}
/**
 * Revoking anything invalidates ALL of the account subtree, not just the
 * list the button was in.
 *
 * That mirrors what the server does: revoking a device kills its bindings,
 * its sessions and its refresh families in one transaction, so a UI that
 * refreshed only the device list would keep showing sessions the platform
 * has already ended.
 */
function useRevocation(fn) {
    const queries = useQueryClient();
    return useMutation({
        mutationFn: fn,
        onSuccess: async () => {
            await queries.invalidateQueries({ queryKey: sessionKeys.all });
        },
    });
}
export function useRevokeDevice() {
    const transport = useTransport();
    return useRevocation((deviceID) => revokeDevice(transport, { deviceID }));
}
/**
 * Retiring goes through useRevocation like the others, because it revokes
 * strictly MORE than they do — the whole account subtree is stale afterwards,
 * including the device row itself.
 */
export function useRetireDevice() {
    const transport = useTransport();
    const queries = useQueryClient();
    return useMutation({
        mutationFn: (deviceID) => retireDevice(transport, { deviceID }),
        onSuccess: async () => {
            await queries.invalidateQueries({ queryKey: sessionKeys.all });
        },
    });
}
/**
 * Restore is NOT a revocation, so it cannot go through useRevocation — the
 * result has no counts to report and nothing was ended. It invalidates the
 * same subtree anyway, and for a reason worth stating: the device row's status
 * changed, and so did whether the sessions list may grow the next time the
 * machine is signed in on.
 */
export function useRestoreDevice() {
    const transport = useTransport();
    const queries = useQueryClient();
    return useMutation({
        mutationFn: (deviceID) => restoreDevice(transport, { deviceID }),
        onSuccess: async () => {
            await queries.invalidateQueries({ queryKey: sessionKeys.all });
        },
    });
}
export function useRevokeBinding() {
    const transport = useTransport();
    return useRevocation((bindingID) => revokeBinding(transport, { bindingID }));
}
export function useRevokeSession() {
    const transport = useTransport();
    return useRevocation((sessionID) => revokeSession(transport, { sessionID }));
}
/**
 * useDecideDeviceApproval is one hook for both decisions because the screen
 * is one screen — but it dispatches to two different ROUTES, which is the
 * server's deliberate shape: "approve" must never be the value a malformed
 * request falls back to.
 */
export function useDecideDeviceApproval() {
    const transport = useTransport();
    const queries = useQueryClient();
    return useMutation({
        mutationFn: ({ approvalID, approve }) => approve ? approveDevice(transport, { approvalID }) : denyDevice(transport, { approvalID }),
        onSuccess: async () => {
            await queries.invalidateQueries({ queryKey: sessionKeys.all });
        },
    });
}
//# sourceMappingURL=hooks.js.map